import { downloadSpreadsheet } from '@/utils/downloadUtils';
import { CloudUpload } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { ColumnExtraction } from './extractColumnEntries';
import { ParsedPartNumberFile } from './parsePartNumberFile';
import { MAX_PART_NUMBERS } from './partNumberValidation';
import { getSamplePartNumberCsvData } from './sampleCsvData';
import { IntakeSummary } from './IntakeSummary';

const PREVIEW_ROW_LIMIT = 10;
const SKIPPED_DISPLAY_LIMIT = 6;

const Dropzone = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '32px',
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'border-color 0.2s ease',
  '&:hover': { borderColor: theme.palette.primary.main },
  '&.active': { borderColor: theme.palette.primary.main, backgroundColor: theme.palette.action.hover },
}));

const PreviewScroller = styled(Box)(({ theme }) => ({
  marginTop: '12px',
  maxHeight: '220px',
  overflow: 'auto',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
}));

const downloadSampleCsv = () => {
  downloadSpreadsheet(getSamplePartNumberCsvData(), { filename: 'part-numbers-template.csv', format: 'csv' });
};

const describeSkippedRows = (skippedRows: ColumnExtraction['skippedRows']): string => {
  const shown = skippedRows
    .slice(0, SKIPPED_DISPLAY_LIMIT)
    .map(skipped => `row ${skipped.row} (${skipped.reason})`)
    .join(', ');
  const remainder = skippedRows.length - SKIPPED_DISPLAY_LIMIT;
  return remainder > 0 ? `${shown}, +${remainder} more` : shown;
};

export interface CsvIntakeProperties {
  file?: ParsedPartNumberFile;
  extraction?: ColumnExtraction;
  isParsing: boolean;
  error?: string;
  exceedsLimit: boolean;
  onFileDropped: (file: File) => void;
  onClear: () => void;
}

export const CsvIntake = ({
  file,
  extraction,
  isParsing,
  error,
  exceedsLimit,
  onFileDropped,
  onClear,
}: CsvIntakeProperties) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: accepted => {
      const dropped = accepted[0];
      if (dropped) onFileDropped(dropped);
    },
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
    disabled: isParsing,
  });

  if (!file) {
    return (
      <Box>
        {error && (
          <Alert severity='error' sx={{ marginBottom: '12px' }}>
            {error}
          </Alert>
        )}
        <Dropzone {...getRootProps()} className={isDragActive ? 'active' : ''} data-testid='csv-intake-dropzone'>
          <input {...getInputProps()} data-testid='csv-intake-input' />
          {isParsing ? <CircularProgress size={24} /> : <CloudUpload color='action' fontSize='large' />}
          <Typography variant='body2'>
            {isDragActive ? 'Drop the file here' : 'Drop CSV / XLSX here, or click to browse'}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            max 10MB, {MAX_PART_NUMBERS} rows
          </Typography>
        </Dropzone>
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', marginTop: '8px' }}>
          Need a template?{' '}
          <Link component='button' type='button' onClick={downloadSampleCsv} data-testid='csv-intake-sample-download'>
            Download a sample CSV
          </Link>
        </Typography>
      </Box>
    );
  }

  const previewRows = file.rows.slice(0, PREVIEW_ROW_LIMIT);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        <Typography variant='body2'>
          {file.filename} · {file.headers.length} columns · {file.rows.length} rows
        </Typography>
        <Button onClick={onClear} size='small' color='inherit' data-testid='csv-intake-clear'>
          Replace
        </Button>
      </Box>
      <PreviewScroller>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              {file.headers.map((header, index) => (
                <TableCell key={`${header}-${index}`} sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>
                  {header || `Column ${index + 1}`}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {previewRows.map((row, rowIndex) => (
              <TableRow key={`${row.join('|')}-${rowIndex}`}>
                {file.headers.map((header, columnIndex) => (
                  <TableCell key={`${header}-${columnIndex}`}>{row[columnIndex] ?? ''}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PreviewScroller>
      {extraction && (
        <IntakeSummary>
          <span>{extraction.validCount} valid</span>
          <span>{extraction.duplicatesRemoved} duplicates</span>
          <span>{extraction.skippedRows.length} invalid</span>
          {extraction.skippedRows.length > 0 && <span>skipped: {describeSkippedRows(extraction.skippedRows)}</span>}
          {exceedsLimit && (
            <Typography component='span' color='error.main' variant='body2'>
              Limit is {MAX_PART_NUMBERS}
            </Typography>
          )}
        </IntakeSummary>
      )}
    </Box>
  );
};
